import { activityTrackerFor, createApiClient, type ApiClient } from '@brush/api-client';
import { PORTAL_SCREENS, type PortalScreenName } from './activity/screens';
import { readActingRole, useAccessToken, useCurrentAccount, useFreshSignIn, useSignOut } from '@brush/auth';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { apiBaseUrl, apiScope } from './authConfig';

const ApiContext = createContext<ApiClient | null>(null);

export interface Session {
  displayName: string | null;
  /** null when auth is disabled locally — there is nothing to sign out of. */
  signOut: (() => void) | null;
  /**
   * A token from a sign-in made just now, which erasing an account requires (ADR 0012 step 28). Under
   * the dev bypass there is nothing to sign in to and the API stamps every request as just signed in.
   */
  signInAgain: () => Promise<string>;
  /** Leaves the portal after an erasure, for the landing page that says it happened. */
  endAfterErasure: () => void;
}

/**
 * The dev landing gate's key (`main.tsx`). Cleared after an erasure, so the reload lands on the landing
 * page the way a real sign-out does.
 */
export const DEV_ENTERED_KEY = 'brush.player.devLandingEntered';

const SessionContext = createContext<Session>({
  displayName: null,
  signOut: null,
  signInAgain: () => Promise.reject(new Error('No session.')),
  endAfterErasure: () => {},
});

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error('useApi must be used within an ApiProvider.');
  return client;
}

export function useSession(): Session {
  return useContext(SessionContext);
}

const DEV_SESSION: Session = {
  displayName: 'Development player',
  signOut: null,
  signInAgain: () => Promise.resolve('dev'),
  endAfterErasure: () => {
    try {
      sessionStorage.removeItem(DEV_ENTERED_KEY);
    } catch {
      // Blocked storage: the reload stays in the app rather than on the landing page.
    }
    window.location.reload();
  },
};

/** Local dev: no MSAL. The dev-bypass API authenticates every request. */
export function DevApiProvider({ children }: { children: ReactNode }) {
  const client = useMemo(
    () =>
      createApiClient({
        baseUrl: apiBaseUrl,
        getAccessToken: () => Promise.resolve('dev'),
        // Read from storage per request rather than captured here: switching role must not
        // require rebuilding the client, and a captured value would be stale the moment it did.
        getActingRole: readActingRole,
      }),
    [],
  );
  return (
    <ApiContext.Provider value={client}>
      <SessionContext.Provider value={DEV_SESSION}>{children}</SessionContext.Provider>
    </ApiContext.Provider>
  );
}

/** Production: real access token from MSAL. Must render inside <AuthProvider> and <RequireAuth>. */
export function MsalApiProvider({ children }: { children: ReactNode }) {
  const getAccessToken = useAccessToken(apiScope);
  const account = useCurrentAccount();
  const signOut = useSignOut();
  const signInAgain = useFreshSignIn(apiScope);

  const client = useMemo(
    () => createApiClient({ baseUrl: apiBaseUrl, getAccessToken, getActingRole: readActingRole }),
    [getAccessToken],
  );
  const session = useMemo<Session>(
    () => ({ displayName: account?.name ?? null, signOut, signInAgain, endAfterErasure: signOut }),
    [account?.name, signOut, signInAgain],
  );

  return (
    <ApiContext.Provider value={client}>
      <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
    </ApiContext.Provider>
  );
}

/**
 * Reports this app's session once it is in use — once per app session, which the tracker keeps in
 * sessionStorage, so a reload is not a second session (ADR 0012 Decision 13). The portal's own code: the
 * server refuses the other app's session from this app's token.
 */
export function useSessionStarted(ready: boolean) {
  const api = useApi();
  useEffect(() => {
    if (ready) activityTrackerFor(api).sessionStarted('portal.session_started');
  }, [api, ready]);
}

/**
 * Reports a portal screen on entry (ADR 0012 step 16) — once per screen per room per app session, which
 * the tracker keeps; a failure is never retried. <b>The room is the card on screen, and null where the
 * screen is about no one room</b>: the cards list spans every room the account holds, and the claim form
 * is opened before any card exists, so the room it offers is one the caller may not read yet — and a
 * view naming an unreadable room is dropped by the server, which would lose the claim form's view.
 */
export function useScreenViewed(screen: PortalScreenName, roomId: string | null) {
  const api = useApi();
  useEffect(() => {
    activityTrackerFor(api).viewed(PORTAL_SCREENS[screen], roomId);
  }, [api, screen, roomId]);
}
